
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SUBMIT_URL = 'https://readdy.ai/api/form/d6j4rjrcs59vsrrrjsqg';
const DISMISSED_KEY = 'meow_sticky_bar_dismissed';

export default function StickyEmailBar() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed) return;

    const handleScroll = () => {
      const scrolled = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      if (scrolled > 0.4) {
        setVisible(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('submitting');
    try {
      const params = new URLSearchParams();
      params.append('email', email);
      params.append('source', 'sticky_bar');
      await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setStatus('success');
      setTimeout(() => {
        setVisible(false);
        localStorage.setItem(DISMISSED_KEY, '1');
      }, 3000);
    } catch {
      setStatus('idle');
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed bottom-0 left-0 right-0 z-[9998]"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        >
          <div className="bg-gray-900 border-t border-gray-700 px-4 py-3 shadow-2xl">
            <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center gap-3 justify-between">

              {status === 'success' ? (
                <div className="flex items-center gap-2 text-teal-400 font-bold text-sm w-full justify-center">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                  You're in! Check your inbox for the free guide 🎉
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-white text-sm font-semibold whitespace-nowrap">
                    <span className="text-lg">🐱</span>
                    <span>Get the <span className="text-teal-400">Free Cat Care Guide</span> — join 12,400+ cat lovers!</span>
                  </div>
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex items-center gap-2 w-full sm:w-auto"
                  >
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      placeholder="Enter your email"
                      className="flex-1 sm:w-56 px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white font-bold rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap"
                    >
                      {status === 'submitting' ? <i className="ri-loader-4-line animate-spin"></i> : 'Get It Free'}
                    </button>
                  </form>
                </>
              )}

              <button
                onClick={handleDismiss}
                className="absolute top-2 right-3 sm:relative sm:top-auto sm:right-auto w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
